import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Inline, Stack, Text } from '@relay-towing/ui';
import { useInspectionReport } from '@/hooks/useTruckInspections';
import type { InspectionStatus, ReportAnswer } from '@/api/truckInspections';
import { answerValue, statusLabel, whenLabel } from '../present';
import { LastCheckSkeleton } from './InspectionSkeletons';

interface Props {
  last: {
    id: string;
    status: InspectionStatus;
    submittedAt?: string | null;
    signedName?: string | null;
    driver?: { name: string } | null;
  };
}

/**
 * The last check filed on this truck, folded shut above the blank form.
 *
 * The next person to look at the truck wants the previous report to hand:
 * what the odometer read, what was already flagged. Open, it is the whole
 * report. Shut, it is one line, because most of the time nobody needs it.
 */
export function LastCheckCard({ last }: Props) {
  const [open, setOpen] = useState(false);
  const report = useInspectionReport(last.id, open);

  const by = last.driver?.name ?? last.signedName;

  const sections: [string, ReportAnswer[]][] = [];
  report.data?.answers.forEach((answer) => {
    const key = answer.itemSection || 'Checklist';
    const found = sections.find(([name]) => name === key);
    if (found) found[1].push(answer);
    else sections.push([key, [answer]]);
  });

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Button
        variant="ghost"
        size="inline"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full justify-between gap-3 p-4 text-left rounded-none"
      >
        <Stack gap={0} className="min-w-0">
          <Text variant="caption" tone="muted">
            Last check · {statusLabel(last.status)}
          </Text>
          <Text weight="medium" className="truncate">
            Checked {whenLabel(last.submittedAt)}
            {by ? ` by ${by}` : ''}
          </Text>
        </Stack>
        {open ? (
          <ChevronUp className="shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="shrink-0 text-muted-foreground" />
        )}
      </Button>

      {open && (
        <div className="border-t border-border p-4">
          {report.isLoading || !report.data ? (
            <LastCheckSkeleton />
          ) : (
            <Stack gap={4}>
              <Inline gap={4} wrap>
                <Stack gap={0}>
                  <Text variant="caption" tone="muted">
                    Result
                  </Text>
                  <Text weight="medium">{statusLabel(report.data.status)}</Text>
                </Stack>
                {report.data.odometer != null && (
                  <Stack gap={0}>
                    <Text variant="caption" tone="muted">
                      Odometer
                    </Text>
                    <Text weight="medium">
                      {report.data.odometer.toLocaleString()}
                    </Text>
                  </Stack>
                )}
              </Inline>

              {sections.map(([section, answers]) => (
                <Stack key={section} gap={1}>
                  <Text variant="overline" tone="muted">
                    {section}
                  </Text>
                  {answers.map((answer) => (
                    <Inline
                      key={answer.id}
                      justify="between"
                      align="start"
                      gap={3}
                      className="border-b border-border py-1.5 last:border-b-0"
                    >
                      <Text variant="caption" className="min-w-0">
                        {answer.itemLabel}
                      </Text>
                      <Text
                        variant="caption"
                        weight="medium"
                        tone={
                          answer.status === 'FAIL' ? 'destructive' : 'default'
                        }
                        className="shrink-0"
                      >
                        {answerValue(answer)}
                      </Text>
                    </Inline>
                  ))}
                </Stack>
              ))}
            </Stack>
          )}
        </div>
      )}
    </div>
  );
}
